import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Form, Image } from "antd";
import Button from "@/common/elements/Button";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledInformationList,
  StyledSectionHeading,
} from "@/common/styles";
import { InmateInformation } from "@/components/InmateHeader/styles";
import {
  getFinalPath,
  getOptionsFromRefList,
  getRefListNameByID,
  isNonEmptyVariable,
} from "@/util/common_function";
import { PHOTOS_BASE_URL } from "@/util/api";
import defaultAvatar from "@/assets/default_avatar.svg";
import { useSelector } from "react-redux";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import TextField from "@/common/elements/InputElements/TextField";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import Select from "@/common/elements/InputElements/Select";
import { MODULES_LIST, SCHEMA_NAME } from "@/util/common_variable";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";

const JailIncidentInvolvedInmateInformation = ({
  bookings,
  from = "bookings",
  showTopBar = true,
  setShowTopBar,
  showBasicInfo = false,
  setShowBasicInfo,
  onSave,
  selectedInmate,
  setSelectInmateDetails,
  disabled = false,
  inmateSearchAPILoading = false,
  isFieldEnable,
  startingPoint="",
}) => {
  const [searchForm] = Form.useForm();
  const isBookingNumber = Form.useWatch("isBookingNumber", searchForm);
  const [inmateDetails, setInmateDetails] = useState(null);
  const formatDate = (date) => (date ? dayjs(date).format("MM/DD/YYYY") : "-");

  useEffect(() => {
    const source = from === "bookings" ? bookings : selectedInmate || {};
    const bio = from === "bookings" ? bookings?.bio?.[0] || {} : source;

    const classification =
      from === "bookings"
        ? bookings?.arrestReports?.[0]?.classificationId
        : source?.classification;

    const classificationName =
      getRefListNameByID("Classifications", classification, false) || "-";

    const bioPhotoPath =
      from === "bookings"
        ? bio?.bioPhotos?.[0]?.photos?.[0]?.fullSizePath
        : source?.bioPhotos?.[0]?.photos?.[0]?.fullSizePath;

    if (from === "bookings") {
      setInmateDetails(() => ({
        inmateName:
          bio?.lName && bio?.fName
            ? `${bio.lName}, ${bio.fName} ${bio.mName || ""}`
            : "-",
        arrestDate: formatDate(bookings?.arrestReports?.[0]?.arrestDate),
        scheduledReleaseDate: formatDate(
          bookings?.arrestReports?.[0]?.scheduledReleaseDate,
        ),
        dob: formatDate(bio?.dob),
        classificationName: classificationName,
        assignedLocation: bookings?.arrestReports?.[0]?.currentLocation || "-",
        currentLocation: bookings?.arrestReports?.[0]?.currentLocation || "-",
        pinNo: bio?.pinNo || "-",
        bookingNumber: bookings?.bookingNumber || "-",
        facilityStatus: bookings?.arrestReports?.[0]?.workhouseStatusId || "-",
        bioPhotoUrl: bioPhotoPath
          ? PHOTOS_BASE_URL + getFinalPath(bioPhotoPath)
          : defaultAvatar,
      }));
    } else {
      setInmateDetails(() => ({
        inmateName:
          (bio?.lName || bio?.lname) && (bio?.fName || bio?.fname)
            ? `${bio.lName || bio?.lname}, ${bio.fName || bio?.fname}${
                bio?.mname ? ` ${bio.mname}` : ""
              }${bio?.suffix ? `, ${bio.suffix}` : ""}`
            : "-",
        arrestDate: formatDate(source?.arrestDate),
        scheduledReleaseDate: formatDate(source?.scheduledReleaseDate),
        dob: formatDate(source?.dob),
        classificationName: classificationName,
        assignedLocation: source?.assignedLocation || "-",
        currentLocation: source?.currentLocation || "-",
        pinNo: source?.pinNo || "-",
        bookingNumber: source?.bookingNumber || "-",
        facilityStatus: source?.facilityStatus || "-",
        bioPhotoUrl: bioPhotoPath
          ? PHOTOS_BASE_URL + getFinalPath(bioPhotoPath)
          : defaultAvatar,
      }));
    }
  }, [from, selectedInmate, bookings]);

  const HandleSearch = () => {
    searchForm
      .validateFields()
      .then((data) => onSave(data))
      .catch();
  };

  const onDifferentBookingClick = () => {
    setShowBasicInfo(true);
    searchForm.resetFields();
    setInmateDetails(() => ({
      inmateName: "-",
      arrestDate: "-",
      scheduledReleaseDate: "-",
      dob: "-",
      classificationName: "-",
      assignedLocation: "-",
      currentLocation: "-",
      pinNo: "-",
      bookingNumber: "-",
      facilityStatus: "-",
      bioPhotoUrl: "-",
    }));
    setSelectInmateDetails(null);
    setShowTopBar(false);
  };

  return (
    <>
      <StyledSectionHeading marginTop="0px">
        <span>Inmate Information</span>
      </StyledSectionHeading>

      {/* Matches ASP: this whole prompt + the Different Booking button only
          appear in Modify mode. In Details/Browse mode neither renders at
          all - not just disabled. */}
      {!showBasicInfo && isFieldEnable && (
        <StyledGridRow gutter={16}>
          <StyledGridCol lg={24}>
            <p>
              The Booking that you selected is {inmateDetails?.inmateName}{" "}
              {isNonEmptyVariable(inmateDetails?.bookingNumber) &&
                `(#${inmateDetails?.bookingNumber})`}
              . if this is the wrong booking, Click diffent booking button
              below{" "}
            </p>
          </StyledGridCol>
          <StyledGridCol>
            <Button onClick={onDifferentBookingClick}>
              Different Booking
            </Button>
          </StyledGridCol>
        </StyledGridRow>
      )}

      {showBasicInfo && (
        <>
          <FieldSecurityProvider
            entity={SCHEMA_NAME.BIO}
            startingPoint={startingPoint}
            bookingsFieldsEnabled={!disabled}
          >
            <Form form={searchForm} disabled={disabled}>
              <StyledGridRow gutter={16}>
                <StyledFlex align="center" style={{ height: "100%" }}>
                  <SecureFormItem
                    fieldName="isBookingNumber"
                    formItemProps={{ 
                      name: "isBookingNumber",
                      valuePropName: "checked",
                      label: "Search On Booking Number",
                      layout: "horizontal",
                      className: "form-item-checkbox-left",
                      style: { marginBottom: "0" }
                    }}
                  >
                    <Checkbox className="input-control-mask" />
                  </SecureFormItem>
                </StyledFlex>
              </StyledGridRow>
              {!isBookingNumber ? (
                <StyledGridRow gutter={16}>
                  <StyledGridCol lg={6}>
                    <SecureFormItem
                      fieldName="lName"
                      formItemProps={{ 
                        name: "lName",
                        label: "Last Name",
                      }}
                    >
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol lg={6}>
                    <SecureFormItem
                      fieldName="fName"
                      formItemProps={{ 
                        name: "fName",
                        label: "First Name",
                      }}
                    >
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol lg={6}>
                    <SecureFormItem
                      fieldName="mName"
                      formItemProps={{ 
                        name: "mName",
                        label: "Middle Name",
                      }}
                    >
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>

                  <StyledGridCol lg={6}>
                    <SecureFormItem
                      fieldName="suffix"
                      formItemProps={{ 
                        name: "suffix",
                        label: "Suffix",
                      }}
                    >
                      <Select
                        options={getOptionsFromRefList("Suffix")}
                        variant="outlined"
                        disabled={disabled}
                      />
                    </SecureFormItem>
                  </StyledGridCol>
                </StyledGridRow>
              ) : (
                <StyledGridRow gutter={16}>
                  <StyledGridCol lg={6}>
                    <SecureFormItem
                      fieldName="bookingNumber"
                      formItemProps={{ 
                        name: "bookingNumber",
                        label: "Booking Number",
                      }}
                    >
                      <TextField />
                    </SecureFormItem>
                  </StyledGridCol>
                </StyledGridRow>
              )}

              <StyledGridRow gutter={16} style={{ marginBottom: "1rem" }}>
                {(from == "facilityLog" ||
                  from === MODULES_LIST.JAIL_INCIDENTS) && (
                  <StyledGridCol>
                    <Button
                      onClick={HandleSearch}
                      loading={inmateSearchAPILoading}
                      disabled={disabled}
                    >
                      search
                    </Button>
                  </StyledGridCol>
                )}
              </StyledGridRow>
            </Form>
          </FieldSecurityProvider>
        </>
      )}

      {showTopBar && (
        <StyledGridRow>
          <StyledGridCol flex="auto" marginBottom="10px" marginTop="10px">
            <InmateInformation>
              <div className="value red">
                <h5 style={{ margin: "0px" }}>
                  {inmateDetails?.inmateName}{" "}
                  {isNonEmptyVariable(inmateDetails?.bookingNumber) &&
                    `(${inmateDetails?.bookingNumber})`}
                </h5>
              </div>
            </InmateInformation>
          </StyledGridCol>
          <StyledGridCol marginTop="10px">
            <StyledFlex align="center" style={{ minHeight: "38px" }}>
              <Button
                type="secondary"
                color="secondary"
                icon={
                  <span style={{ fontSize: "22px", marginBottom: "10px" }}>
                    ...
                  </span>
                }
              />
            </StyledFlex>
          </StyledGridCol>
        </StyledGridRow>
      )}

      <StyledInformationList style={{ padding: "0px" }}>
        <div
          className="result--box result--full-width"
          style={{ paddingTop: "8px" }}
        >
          <StyledGridRow gutter={16}>
            <StyledGridCol lg={6}>
              <div style={{ textAlign: "center" }}>
                <Image
                  src={inmateDetails?.bioPhotoUrl}
                  alt="Inmate Photo"
                  width={200}
                  height={200}
                  fallback={defaultAvatar}
                />
              </div>
            </StyledGridCol>
            <StyledGridCol lg={18}>
              <StyledGridRow gutter={16}>
                <StyledGridCol span={8}>
                  <div className="key">Arrest Date</div>
                  <p className="value">{inmateDetails?.arrestDate}</p>
                </StyledGridCol>
                <StyledGridCol span={8}>
                  <div className="key">Sched. Rel. Date</div>
                  <p className="value">{inmateDetails?.scheduledReleaseDate}</p>
                </StyledGridCol>
                <StyledGridCol span={8}>
                  <div className="key">Classification</div>
                  <p className="value">{inmateDetails?.classificationName}</p>
                </StyledGridCol>
                <StyledGridCol span={8}>
                  <div className="key">Assigned Location</div>
                  <p className="value">{inmateDetails?.assignedLocation}</p>
                </StyledGridCol>
                <StyledGridCol span={8}>
                  <div className="key">Current Location</div>
                  <p className="value">{inmateDetails?.currentLocation}</p>
                </StyledGridCol>
                <StyledGridCol span={8}>
                  <div className="key">Pin No</div>
                  <p className="value">{inmateDetails?.pinNo}</p>
                </StyledGridCol>
                <StyledGridCol span={8}>
                  <div className="key">Booking Number</div>
                  <p className="value">{inmateDetails?.bookingNumber}</p>
                </StyledGridCol>
                <StyledGridCol span={8}>
                  <div className="key">Facility Status</div>
                  <p className="value">{inmateDetails?.facilityStatus}</p>
                </StyledGridCol>
                <StyledGridCol span={8}>
                  <div className="key">DOB</div>
                  <p className="value">{inmateDetails?.dob}</p>
                </StyledGridCol>
              </StyledGridRow>
            </StyledGridCol>
          </StyledGridRow>
        </div>
      </StyledInformationList>
    </>
  );
};

export default JailIncidentInvolvedInmateInformation;
